import ApartmentIcon from '@mui/icons-material/Apartment';
import BuildIcon from '@mui/icons-material/Build';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SortIcon from '@mui/icons-material/Sort';
import StoreIcon from '@mui/icons-material/Store';
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { Expense, Vehicle } from '@otw/models';
import { formatPrice } from 'core/utils/inventoryUtils';
import dayjs from 'dayjs';
import { useIsMobile } from 'hooks/screen/useIsMobile';
import React, { useMemo, useState } from 'react';

interface EnrichedExpense extends Expense {
  linkedVehicleData?: Vehicle | null;
}

interface ExpenseListProps {
  expenses: EnrichedExpense[];
  onDelete: (id: string) => Promise<void>;
  onEdit?: (expense: EnrichedExpense) => void;
}

export const ExpenseList: React.FC<ExpenseListProps> = ({ expenses, onDelete, onEdit }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExpenseId, setSelectedExpenseId] = useState<string | null>(null);
  const [selectedExpense, setSelectedExpense] = useState<EnrichedExpense | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [expandedExpenseId, setExpandedExpenseId] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const theme = useTheme();

  // Function to get vehicle details from vehicleId (used when linkedVehicleData is missing)
  const getVehicleDetails = (expense: EnrichedExpense) => {
    if (expense.linkedVehicleData) return expense.linkedVehicleData
    return null
  };

  const handleDeleteClick = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedExpenseId(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedExpenseId) {
      await onDelete(selectedExpenseId);
      setDeleteDialogOpen(false);
      setSelectedExpenseId(null);
      if (detailDialogOpen && selectedExpense?.id === selectedExpenseId) {
        setDetailDialogOpen(false);
        setSelectedExpense(null);
      }
      if (expandedExpenseId === selectedExpenseId) {
        setExpandedExpenseId(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedExpenseId(null);
  };

  const handleExpenseClick = (expense: EnrichedExpense, event: React.MouseEvent) => {
    event.stopPropagation();
    if (isMobile) {
      setExpandedExpenseId(expandedExpenseId === expense.id ? null : expense.id);
    } else {
      setSelectedExpense(expense);
      setDetailDialogOpen(true);
    }
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedExpense(null);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Shipping':
        return <LocalShippingIcon fontSize="small" />;
      case 'Repairs':
        return <BuildIcon fontSize="small" />;
      case 'RentalReimbursement':
        return <DirectionsCarIcon fontSize="small" />;
      case 'Other':
        return <ApartmentIcon fontSize="small" />;
      default:
        // Always return a React element, never null
        return <ApartmentIcon fontSize="small" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Shipping':
        return 'primary';
      case 'Repairs':
        return 'error';
      case 'RentalReimbursement':
        return 'success';
      case 'Other':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getCategoryBgColor = (category: string) => {
    switch (category) {
      case 'Shipping':
        return theme.palette.primary.light;
      case 'Repairs':
        return theme.palette.error.light;
      case 'RentalReimbursement':
        return theme.palette.success.light;
      case 'Other':
        return theme.palette.warning.light;
      default:
        return theme.palette.grey[200];
    }
  };

  const processedExpenses = useMemo(() => {
    // Log if there are any expenses with vehicleId but missing linkedVehicleData
    const missingVehicleData = expenses.filter(exp => exp.vehicleId && !exp.linkedVehicleData);
    if (missingVehicleData.length > 0) {
      console.warn('Found expenses with vehicleId but missing linkedVehicleData:', missingVehicleData);
    }
    return expenses;
  }, [expenses]);

  const groupedByDate = useMemo(() => {
    return processedExpenses.reduce((groups, expense) => {
      const date = dayjs(expense.date).format('MMMM D, YYYY');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(expense);
      return groups;
    }, {} as Record<string, EnrichedExpense[]>);
  }, [processedExpenses]);

  const totalAmount = useMemo(() => {
    return processedExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  }, [processedExpenses]);

  if (expenses.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body1">No expenses found for current filter.</Typography>
      </Box>
    );
  }

  return (
    <>
      {isMobile ? (
        <Box>
          {/* Total Amount Display */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1">
              {processedExpenses.length} {processedExpenses.length === 1 ? 'expense' : 'expenses'}
            </Typography>
            <Typography variant="subtitle1" fontWeight="bold">
              Total: {formatPrice(totalAmount)}
            </Typography>
          </Box>

          {/* Expense Cards Grouped by Date */}
          <Stack spacing={2}>
            {Object.entries(groupedByDate).map(([date, dateExpenses]) => (
              <Box key={date}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 1, 
                    pb: 0.5, 
                    borderBottom: '1px solid', 
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon fontSize="inherit" sx={{ mr: 0.5, color: 'text.secondary' }} />
                    {date}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {dateExpenses.length} {dateExpenses.length === 1 ? 'item' : 'items'}
                  </Typography>
                </Typography>
                <Stack spacing={1.5}>
                  {dateExpenses.map((expense: EnrichedExpense) => (
                    <Card 
                      key={expense.id} 
                      elevation={1} 
                      onClick={(e) => handleExpenseClick(expense, e)}
                      sx={{
                        cursor: 'pointer',
                        borderLeft: `4px solid ${getCategoryBgColor(expense.category)}`,
                        '&:hover': {
                          boxShadow: theme.shadows[4],
                        }
                      }}
                    >
                      <CardContent sx={{ pb: '16px !important' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Chip 
                              icon={getCategoryIcon(expense.category)} 
                              label={expense.customCategory || expense.category} 
                              size="small" 
                              color={getCategoryColor(expense.category)}
                              sx={{ mb: 0.5, fontWeight: 'medium' }}
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {expense.description}
                            </Typography>
                            {expense.linkedVehicleData && (
                              <Tooltip title={`VIN: ${expense.linkedVehicleData.vin || 'N/A'}`}>
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                  <LinkIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                                  {expense.linkedVehicleData.year} {expense.linkedVehicleData.make} {expense.linkedVehicleData.model} ({expense.linkedVehicleData.size || 'N/A'})
                                  {expense.linkedVehicleData.status === 'Sold' && expense.linkedVehicleData.sold_date && (
                                    <Typography variant="caption" color="success.main" sx={{ ml: 0.5 }}>
                                      (Sold: {dayjs(expense.linkedVehicleData.sold_date).format('MMM D, YYYY')})
                                    </Typography>
                                  )}
                                </Typography>
                              </Tooltip>
                            )}
                             <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                              Entered: {dayjs(expense.created_at).format('MMM D, YYYY h:mm A')}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right', ml: 1 }}>
                            <Typography variant="h6" fontWeight="bold" color={`${getCategoryColor(expense.category)}.dark`}>
                              {formatPrice(expense.amount)}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                              {onEdit && (
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit(expense);
                                  }} 
                                  color="primary"
                                  sx={{ mr: 0.5 }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton 
                                size="small" 
                                onClick={(e) => handleDeleteClick(expense.id, e)} 
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                        <Collapse in={expandedExpenseId === expense.id} timeout="auto" unmountOnExit>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            <strong>Details:</strong>
                          </Typography>
                          {/* Vehicle information from either linkedVehicleData or direct lookup */}
                          {(expense.linkedVehicleData || getVehicleDetails(expense)) && (
                            <>
                              {(() => {
                                const vehicleData = getVehicleDetails(expense);
                                return vehicleData ? (
                                  <>
                                    <Typography variant="caption" color="text.secondary" component="div">
                                      Stock #: {vehicleData.stock_number || 'N/A'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" component="div">
                                      Bought from: {vehicleData.bought_from || 'N/A'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" component="div">
                                      Sold to: {vehicleData.sold_to_user ? vehicleData.sold_to_user.name : 'N/A'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" component="div">
                                      {vehicleData.year} {vehicleData.make} {vehicleData.model}
                                    </Typography>
                                  </>
                                ) : null;
                              })()}
                            </>
                          )}
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" color="text.secondary" component="div">
                            Expense Date: {dayjs(expense.date).format('MMM D, YYYY')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="div">
                            Expense ID: {expense.id}
                          </Typography>
                        </Collapse>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Linked Vehicle</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Expense Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Entered On</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Sold Date</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedExpenses.map((expense: EnrichedExpense) => (
                <TableRow 
                  key={expense.id} 
                  hover 
                  onClick={(e) => handleExpenseClick(expense, e)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.1) } 
                  }}
                >
                  <TableCell>
                    <Chip 
                      icon={getCategoryIcon(expense.category)} 
                      label={expense.customCategory || expense.category} 
                      size="small" 
                      color={getCategoryColor(expense.category)}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={expense.description}>
                      <span>{expense.description}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {expense.linkedVehicleData ? (
                      <Tooltip title={`VIN: ${expense.linkedVehicleData.vin || 'N/A'}\nStock #: ${expense.linkedVehicleData.stock_number || 'N/A'}`}>
                        <Typography variant="body2">
                          {expense.linkedVehicleData.year} {expense.linkedVehicleData.make} {expense.linkedVehicleData.model}
                          <br/>
                          <Typography variant="caption" color="text.secondary">
                            Stock #: {expense.linkedVehicleData.stock_number || 'N/A'}
                          </Typography>
                          <br/>
                          <Typography variant="caption" color="text.secondary">
                            VIN: {expense.linkedVehicleData.vin || 'N/A'}
                          </Typography>
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.disabled">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium' }}>{formatPrice(expense.amount)}</TableCell>
                  <TableCell>{dayjs(expense.date).format('MMM D, YYYY')}</TableCell>
                  <TableCell>{dayjs(expense.created_at).format('MMM D, YYYY h:mm A')}</TableCell>
                  <TableCell>
                    {expense.linkedVehicleData && expense.linkedVehicleData.status === 'Sold' && expense.linkedVehicleData.sold_date ? (
                      dayjs(expense.linkedVehicleData.sold_date).format('MMM D, YYYY')
                    ) : (
                      <Typography variant="body2" color="text.disabled">N/A</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      {onEdit && (
                        <Tooltip title="Edit Expense">
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(expense);
                            }} 
                            color="primary"
                            sx={{ mr: 0.5 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete Expense">
                        <IconButton size="small" onClick={(e) => handleDeleteClick(expense.id, e)} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Expense Detail Dialog (Desktop) */}
      {selectedExpense && (
        <Dialog open={detailDialogOpen} onClose={handleCloseDetailDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ bgcolor: getCategoryBgColor(selectedExpense.category), color: getCategoryColor(selectedExpense.category) === 'default' ? 'inherit' : `${getCategoryColor(selectedExpense.category)}.contrastText` }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {getCategoryIcon(selectedExpense.category)}
              <Typography variant="h6" component="span" sx={{ ml: 1 }}>
                {selectedExpense.customCategory || selectedExpense.category} Expense Details
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>Description</Typography>
                <Typography variant="body1">{selectedExpense.description}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>Amount</Typography>
                <Typography variant="body1" fontWeight="bold">{formatPrice(selectedExpense.amount)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>Expense Date</Typography>
                <Typography variant="body1">{dayjs(selectedExpense.date).format('MMMM D, YYYY')}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>Entered On</Typography>
                <Typography variant="body1">{dayjs(selectedExpense.created_at).format('MMMM D, YYYY h:mm A')}</Typography>
              </Grid>
              {selectedExpense.linkedVehicleData && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1" gutterBottom>Linked Vehicle</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Vehicle</Typography>
                    <Typography variant="body1">
                      {selectedExpense.linkedVehicleData.year} {selectedExpense.linkedVehicleData.make} {selectedExpense.linkedVehicleData.model}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Size: {selectedExpense.linkedVehicleData.size || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Stock # / VIN</Typography>
                    <Typography variant="body1">{selectedExpense.linkedVehicleData.stock_number || 'N/A'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      VIN: {selectedExpense.linkedVehicleData.vin || 'N/A'}
                    </Typography>
                  </Grid>
                  {selectedExpense.linkedVehicleData.status === 'Sold' && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2">Status</Typography>
                      <Typography variant="body1" color="success.main">
                        Sold on: {selectedExpense.linkedVehicleData.sold_date ? dayjs(selectedExpense.linkedVehicleData.sold_date).format('MMMM D, YYYY') : 'N/A'}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}
              <Grid item xs={12}>
                 <Divider sx={{ my: 1 }} />
                 <Typography variant="caption" color="text.disabled">Expense ID: {selectedExpense.id}</Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(selectedExpense.id, e);
              }} 
              color="error"
              startIcon={<DeleteIcon />}
            >
              Delete
            </Button>
            {onEdit && (
              <Button 
                onClick={() => {
                  handleCloseDetailDialog();
                  onEdit(selectedExpense);
                }}
                color="primary"
                startIcon={<EditIcon />}
              >
                Edit
              </Button>
            )}
            <Button onClick={handleCloseDetailDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        fullWidth={isMobile}
        maxWidth="xs"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this expense? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExpenseList; 